import { useState } from "react";
import { Link } from "react-router-dom";

import type { FollowSignal, FollowSummary, ScenarioStatus } from "../api/types";
import { Empty, FoldablePanel, Note, Panel } from "../components/Panel";
import { PnLShape } from "../components/PnLShape";
import { RateCell, RateHead } from "../components/RateCell";
import { useJournalShapes } from "../hooks/useShapes";
import { fragility, fragilityTone, rateTone, rateWord, shapeOf } from "../lib/shape";
import { FollowModeSwitch } from "../components/FollowModeSwitch";
import { ScenarioModeSwitch } from "../components/ScenarioModeSwitch";
import { DecidedFeed, DecidedPanel } from "../components/DecidedPanel";
import { CohortFeed, CohortPanel } from "../components/CohortPanel";
import { ConsensusFeed, ConsensusPanel } from "../components/ConsensusPanel";
import { FamilyFeed, FamilyPanel, FamilyRoster } from "../components/FamilyPanel";
import { ShiftFeed, ShiftPanel } from "../components/ShiftPanel";
import { PairPanel } from "../components/PairPanel";
import { AIComparePanel } from "../components/AIComparePanel";
import { MispricedGridPanel } from "../components/MispricedGrid";
import { MispricedFeed, MispricedPanel } from "../components/MispricedPanel";
import { useFollowSummary, useSignalHistory } from "../hooks/useSignalFeed";
import { useStatus } from "../hooks/useStatus";
import {
  against,
  depthLabel,
  emptyFeedReason,
  historySignals,
  hypotheses,
  ofWindowLen,
  outcomeLabel,
  ruleLabel,
  scenarioLabel,
  signedDollars,
  signTone,
  toneColor,
  verdict,
  walletLabel,
} from "../lib/signals";
import { formatDollars } from "../lib/tape";
import { useSignals } from "../store/useSignals";

const WINDOWS: readonly { label: string; hours: number }[] = [
  { label: "сутки", hours: 24 },
  { label: "3д", hours: 72 },
  { label: "7д", hours: 168 },
  { label: "30д", hours: 720 },
];

/**
 * Длительности окна серии, по которым режется вся страница.
 *
 * Ноль означает «все» и стои́т ПОСЛЕДНИМ, а не первым: он же умолчание, и
 * читать список слева направо оператор будет как «5м, 15м, а можно и всё».
 *
 * Секунды, а не «5m»: тем же числом величина лежит в базе (window_len_s) и
 * едет в запросе, и вторая её запись означала бы перевод в двух местах.
 * Длительность ОКНА — не то же, что window_s журналов: там ширина метрики
 * резолва (TWAP-30/60), и путать их нельзя (см. миграцию 00038).
 */
const WINDOW_LENS: readonly { label: string; sec: number }[] = [
  { label: "5м", sec: 300 },
  { label: "15м", sec: 900 },
  { label: "все", sec: 0 },
];

/**
 * Экран копирования ставок умных кошельков.
 *
 * Ради верхней панели работа и делалась: она отвечает на два вопроса сразу —
 * плюсовая ли гипотеза вообще и тем ли ордером мы входим. Спорить об этом
 * бесполезно, можно только измерить, и здесь измерение и лежит.
 */
export function SignalsPage() {
  const [hours, setHours] = useState(168);
  // Длительность окна — состояние СТРАНИЦЫ, рядом с окном сводок: обе величины
  // режут одну и ту же выборку, и разведённые по панелям они означали бы, что
  // соседние сводки посчитаны по разным множествам строк.
  //
  // Умолчание — «все окна»: страница обязана открываться тем же ответом, что
  // давала до появления фильтра, иначе пропавшие строки читались бы как
  // сломанная подсистема.
  const [windowLen, setWindowLen] = useState(0);

  return (
    <div className="flex flex-col gap-4">
      {/* Окно сводок — НАД панелями, а не внутри одной из них. Раньше оно жило в
          карточке «Две гипотезы», и это работало ровно до тех пор, пока та
          карточка была первой на экране: свернув её, оператор потерял бы
          переключатель, которым режутся все шесть сводок ниже. */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
        <span>окно сводок:</span>
        {WINDOWS.map((w) => (
          <button
            key={w.hours}
            type="button"
            onClick={() => setHours(w.hours)}
            className={`rounded border px-2 py-0.5 ${
              hours === w.hours
                ? "border-[var(--color-accent)] text-[var(--color-accent)]"
                : "border-[var(--color-border)] text-[var(--color-muted)]"
            }`}
          >
            {w.label}
          </button>
        ))}

        {/* Длительность окна — РЯДОМ с окном сводок, а не отдельной строкой:
            это два среза одной выборки, и прочитать их надо вместе. Разделитель
            нужен ровно затем, чтобы «30д» и «5м» не слились в один ряд кнопок:
            обе подписи про время, и без зазора вторая читается как ещё одна
            ступень первой. */}
        <span className="mx-1 text-[var(--color-border)]">|</span>
        <span>длительность окна:</span>
        {WINDOW_LENS.map((w) => (
          <button
            key={w.sec}
            type="button"
            onClick={() => setWindowLen(w.sec)}
            className={`rounded border px-2 py-0.5 ${
              windowLen === w.sec
                ? "border-[var(--color-accent)] text-[var(--color-accent)]"
                : "border-[var(--color-border)] text-[var(--color-muted)]"
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {/* Консенсус семьи кошельков — ОБНОВЛЁННАЯ «Копирование ставок» и
          РЕНОВИРОВАННЫЙ заголовок экрана, поэтому первая среди живых сигналов,
          хотя исторически подсистема самая молодая. Заменяет собой прежнее
          копирование по ДОХОДНОСТИ кошелька: то измерено и провалено (шесть
          сценариев, 1730 сведённых сигналов, кромки нет ни в одном — перенос
          ROI с недели на неделю r = 0.11). Здесь копируется не кошелёк, а
          СЕМЬЯ — группа адресов одного оператора, найденная по совпадению
          секунды входа, — и отбирается остатком над физикой (перенос r = 0.62,
          вшестеро надёжнее прибыли), а не итогом. Подробности и цифры замера
          вне выборки — в components/FamilyPanel.tsx.

          Обычный Panel, а не FoldablePanel: это головная подсистема экрана
          сейчас, и прятать её за клик значило бы отвести ей то же место в
          интерфейсе, какое здесь занимало провалившееся прежнее измерение.
          Прежний замер архивирован ВНУТРИ, а не удалён — решение оператора уже
          принято: заменить панель, а измерение оставить свёрнутым рядом. */}
      <Panel
        title="Копирование ставок"
        subtitle="теперь — консенсус семьи кошельков; вне выборки 0.742 против цены 0.699 и физики 0.691"
      >
        <div className="flex flex-col gap-5">
          <FamilyPanel hours={hours} windowLen={windowLen} />
          <FamilyRoster />
          {/* Шесть сценариев по доходности — ЗАКРЫТЫЙ вопрос, а не удалённый.
              Сбор ОСТАНОВЛЕН 2026-08-28 решением оператора, а не поломкой:
              причина провала названа (отбор по доходности, перенос r = 0.11),
              и продолжать набирать строки тем же отбором значило бы платить за
              уже полученный ответ. Данные при этом целы и лежат здесь же;
              вернуть сбор — правкой одного FOLLOW_SCENARIOS, без миграций.
              Замер: 1730 сведённых сигналов, на исполнившемся мейкере два
              сценария значимо ОТРИЦАТЕЛЬНЫ (legacy −$196 при t = −2.4,
              passive_profit −$157 при t = −2.5 — неблагоприятный отбор, а не
              невезение), остальные четыре неотличимы от нуля.
              FollowLegacy — ОТДЕЛЬНЫЙ компонент намеренно: под FoldablePanel
              неразвёрнутое тело НЕ МОНТИРУЕТСЯ, и его три хука (сводка, ноты
              трейдера, лента сигналов) не заводят опроса, пока блок свёрнут —
              свёрнутость здесь означает «не ходит в сеть», а не только
              «не видно». Ломать это нельзя. */}
          <FoldablePanel
            title="прежний замер: шесть сценариев"
            badge="остановлен"
            subtitle="кромки нет ни у одного; сбор остановлен решением 2026-08-28, данные целы"
          >
            <FollowLegacy hours={hours} windowLen={windowLen} />
          </FoldablePanel>
        </div>
      </Panel>

      {/* Теневая покупка почти решённого — ДРУГАЯ подсистема, живущая на этом же
          экране. Общего с копированием у неё только слово «сигнал»: там мы идём
          за чужим кошельком, здесь — за собственным расчётом исхода окна.
          Отдельные панели, а не строка в таблице сценариев, именно поэтому:
          сложить их в один итог значило бы сравнить несравнимое. */}
      <DecidedPanel hours={hours} windowLen={windowLen} />

      {/* Котировка когорты плюсовых тейкеров — ЧЕТВЁРТАЯ подсистема на этом
          экране, и стои́т она на месте перевеса группы по решению оператора
          2026-09-02: перевес свой ответ дал (ноль), а этот вопрос открыт.

          От перевеса отличается тем, ЧТО считается по той же группе. Там —
          сколько денег она занесла; здесь — ПО КАКОЙ ЦЕНЕ входила, и второе из
          первого не выводится: группа, купившая только Down по 0.2, стоит в
          лонге Down и котирует Up 0.800. Множество кошельков тоже уже —
          подгруппа «плюсовые тейкеры», а не плюсовые целиком, — поэтому сводки
          двух журналов по составу несравнимы и складывать их нельзя.

          Обычный Panel, а не FoldablePanel: это открытый вопрос экрана, и
          прятать его за клик значило бы отвести ему то же место, какое здесь
          занимают закрытые замеры. */}
      <ConsensusPanel hours={hours} windowLen={windowLen} />

      {/* Смещённый край — ПЯТАЯ подсистема на этом экране, и отличается она от
          соседей тем, ЧТО служит сигналом. У почти решённого и расхождения
          сигнал даёт наша модель, у перевеса группы — чужие кошельки; здесь
          сигналом служит сама цена, и согласия модели вход не требует. Так замер
          остаётся тестом РЫНКА, а не нашего замка, — а замок едет рядом
          контрольной колонкой. Отдельная панель по тому же доводу, что у
          соседей: общий итог сложил бы четыре разных утверждения о рынке. */}
      <ShiftPanel hours={hours} windowLen={windowLen} />

      {/* Пара дешевле доллара — ШЕСТАЯ подсистема, и она единственная, чей
          сигнал не зависит от исхода вовсе. Пара Up + Down гасится ровно в
          доллар, поэтому купивший обе ноги дешевле доллара забирает разницу при
          любом победителе: ни предсказания, ни очереди в стакане затея не
          требует. Отдельная панель по тому же доводу, что у соседей. */}
      <PairPanel hours={hours} windowLen={windowLen} />

      {/* ИИ-судья — не подсистема с гипотезой, а два внешних провайдера,
          которых спрашивает оператор. Стоит здесь, потому что вопрос тот же,
          что у соседей («есть ли кромка»), и сравнивать его ответы удобно
          рядом с их сводками. Окно длины окна к нему не применяется: запросы
          задаёт человек, а не обход. */}
      <AIComparePanel hours={hours} />

      {/* Ленты — ПОД всеми сводками и СВЁРНУТЫЕ.
          Порядок тот же, что был: сводки читают в сравнении друг с другом, и
          разделённые лентой они перестают сравниваться глазами. Свёрнутость —
          про другое: страница отвечает на вопрос «есть ли кромка», а лента — на
          «что происходило», и второй задают, когда первый уже получил ответ.
          Каждая лента опрашивала сервер раз в пятнадцать секунд просто потому,
          что была на экране; теперь свёрнутая не ходит в сеть вовсе. */}
      {/* Лента смещения рядом с соседними: свёрнутая она не монтируется и
          опроса не заводит. */}
      <ShiftFeed hours={hours} windowLen={windowLen} />
      {/* Перевес группы «плюсовых» — сводка ЗАКРЫТОГО замера, и стои́т она здесь,
          а не среди живых, по решению оператора 2026-09-02: вопрос «стоит ли
          вставать за перевесом группы» получил ответ, и ответ этот ноль —
          +0.3 п.п. против цены входа при z = 0.33 на 800 сведённых строках, и ни
          один срез не вышел за 1.3σ.

          Ниже живых сводок по тому же доводу, что и расхождение: их читают В
          СРАВНЕНИИ друг с другом, и закрытая между ними заставляла бы сравнивать
          себя с работающими. Свёрнутая — потому что тело не монтируется, и три
          её хука перестают опрашивать сервер раз в пятнадцать секунд просто за
          то, что панель на экране.

          Не удалена намеренно: замер продолжает идти, строки пишутся, и
          вернуться к нему можно одним разворотом. Место наверху занял вопрос,
          который ещё открыт, — котировка той же когорты. */}
      <CohortPanel hours={hours} windowLen={windowLen} />
      {/* Расхождение модели и стакана — сводка РЕТИРИРОВАННОЙ подсистемы, и
          стои́т она здесь, а не среди живых, по решению оператора: сигнал признан
          неудачным.

          Ниже живых сводок по тому же доводу, по которому те стоят рядом друг с
          другом: их читают В СРАВНЕНИИ, и ретирированная между ними заставляла бы
          сравнивать себя с работающими. Свёрнутая — по доводу этого блока: тело
          не монтируется, и два её хука перестают опрашивать сервер раз в
          пятнадцать секунд просто за то, что панель на экране.

          Не удалена намеренно: замер продолжает идти, строки пишутся, и вернуться
          к нему можно одним разворотом — а вместе со своей сеткой и лентой она
          читается тут же, чего не было, пока сводка стояла наверху. */}
      <MispricedPanel hours={hours} windowLen={windowLen} />
      {/* Сетка порога кромки — НЕ лента, но живёт здесь по обоим правилам этого
          блока. Свёрнутой она не ходит в сеть, а вопрос у неё второго порядка:
          сводки выше отвечают «есть ли кромка», сетка — «не мал ли порог, под
          которым её мерили», и задают его, когда первый ответ уже получен.
          Рядом со своей лентой, а не выше сводок: между сводками она разорвала
          бы их сравнение глазами.

          Длительность окна ей передаётся по тому же доводу, что и соседям:
          населённая другой выборкой, она отвечала бы на вопрос о пороге по
          одному набору окон, а сводка над ней — по другому. */}
      <MispricedGridPanel hours={hours} windowLen={windowLen} />
      <MispricedFeed hours={hours} windowLen={windowLen} />
      <ConsensusFeed hours={hours} />
      <CohortFeed hours={hours} windowLen={windowLen} />
      <DecidedFeed hours={hours} windowLen={windowLen} />
      <FamilyFeed hours={hours} windowLen={windowLen} />
    </div>
  );
}

/**
 * Копирование ставок целиком: режим, две гипотезы, сценарии и лента.
 *
 * ОТДЕЛЬНЫЙ компонент, потому что под FoldablePanel он монтируется только
 * открытым — а вместе с ним и три его запроса: сводка, ноты трейдера и лента
 * сигналов. Оставь эти хуки на странице, и свёрнутый раздел продолжал бы
 * опрашивать сервер ровно так же, как открытый; свёрнутость перестала бы что-то
 * значить, кроме «не видно».
 *
 * Внутри — заголовки, а не вложенные карточки: панель в панели рисует вторую
 * рамку вокруг первой, и раздел выглядит сломанным ещё до того, как в него
 * заглянут.
 *
 * # Почему лента режется длительностью В БРАУЗЕРЕ, а сводка — на сервере
 *
 * Потому что лента живёт не в своём запросе. Карточки копирования лежат в общем
 * хранилище useSignals, куда их кладут ДВА источника: этот исторический запрос и
 * опрос живых карточек, который идёт на всей странице и о фильтре не знает.
 * Отрежь мы историю на сервере — живые карточки чужой длительности всё равно
 * приехали бы в то же хранилище и попали в таблицу. Значит резать надо там, где
 * список показывается, а не там, где он берётся.
 */
function FollowLegacy({ hours, windowLen }: { hours: number; windowLen: number }) {
  const summary = useFollowSummary(hours, windowLen);
  const history = useSignalHistory(24);
  const signals = useSignals((s) => s.signals);
  // Размер реестра — из нот трейдера: без него пустая лента не отличает «китов
  // не встречали» от «в реестре некого встречать».
  const status = useStatus();

  const rows = ofWindowLen(historySignals(Object.values(signals)), windowLen);

  return (
    <div className="flex flex-col gap-5">
      <section>
        <h3 className="mb-2 text-xs font-medium text-[var(--color-muted)]">
          режим — кто в реестре, тот и ведёт
        </h3>
        <FollowModeSwitch />
      </section>

      <section>
        <h3 className="mb-2 text-xs font-medium text-[var(--color-muted)]">
          две гипотезы{summary.data ? ` — ${verdict(summary.data.summary)}` : ""}
        </h3>
        {summary.data ? (
          <>
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-2 font-normal">вход</th>
                  <th className="pb-2 text-right font-normal">сигналов</th>
                  <th className="pb-2 text-right font-normal">результат</th>
                </tr>
              </thead>
              <tbody>
                {hypotheses(summary.data.summary).map((h) => (
                  <tr key={h.key} className="border-t border-[var(--color-border)]">
                    <td className="py-1.5">{h.label}</td>
                    <td className="num py-1.5 text-right">{h.count}</td>
                    <td className="num py-1.5 text-right">{h.empty ? "—" : h.pnl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Note label="как читать: чем гипотезы отличаются знаменателем">
              Сигналов за период <span className="num">{summary.data.summary.signals}</span>,
              сведено <span className="num">{summary.data.summary.settled}</span>, угадали
              исход <span className="num">{summary.data.summary.won}</span>. Ордеров
              выставлено <span className="num">{summary.data.summary.placed}</span> — из них в
              строке результата те, чьё окно уже свелось и чей ордер исполнился. Число
              исполнившихся мейкерских входов — <b>верхняя граница</b>: очередь в стакане не
              моделируется, признаком служит принт по нашей цене или лучше. Тейкерская
              гипотеза считается только по сигналам, у которых стакан отдавал хотя бы
              рыночный минимум: размер и потраченное берутся проходом по уровням, и там,
              где на лучшем предложении нет даже минимума, входа просто не существует.
              Поэтому число сигналов у гипотез разное, и сравнивать суммы можно лишь с
              оглядкой на него.
            </Note>
          </>
        ) : (
          <Empty>{summary.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
        )}
      </section>

      <ScenarioTable
        scenarios={status.data?.follow_scenarios}
        summary={summary.data?.scenarios}
        hours={hours}
        windowLen={windowLen}
      />

      <section>
        <h3 className="mb-2 text-xs font-medium text-[var(--color-muted)]">
          сигналы за сутки
        </h3>
        {rows.length === 0 ? (
          <Empty>
            {history.isError
              ? "лента сигналов не загрузилась"
              : emptyFeedReason(status.data?.follow_wallets)}
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--color-panel)]">
                <tr className="text-left text-[var(--color-muted)]">
                  <th className="pb-2 font-normal">когда</th>
                  <th className="pb-2 font-normal">сценарий</th>
                  <th className="pb-2 font-normal">кит</th>
                  <th className="pb-2 text-right font-normal">ср. ставка</th>
                  <th className="pb-2 text-right font-normal">PnL кита</th>
                  <th className="pb-2 font-normal">рынок</th>
                  <th className="pb-2 font-normal">исход</th>
                  <th className="pb-2 text-right font-normal">ставка</th>
                  <th className="pb-2 text-right font-normal">наш план</th>
                  <th className="pb-2 font-normal">что стало</th>
                  <th className="pb-2 text-right font-normal">мейкер</th>
                  <th className="pb-2 text-right font-normal">тейкер</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <Row key={s.id} signal={s} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Сводка ПО СЦЕНАРИЯМ — то, ради чего работа и делалась.
 *
 * Блок «две гипотезы» выше отвечает «сколько подсистема наработала всего», и
 * на двух правилах сразу это вопрос, которого никто не задавал: сценарии живут
 * на одном потоке ровно затем, чтобы их СРАВНИТЬ. Общий итог прячет именно то,
 * что нужно.
 *
 * Список сценариев приходит из НОТ трейдера, а числа — из журнала, и это разные
 * источники намеренно: трейдер единственный, кто вправе называть потолки, а
 * считать результат по его памяти нельзя — она обнуляется рестартом.
 *
 * Секция, а не карточка: раздел живёт ВНУТРИ свёрнутого блока копирования, и
 * вторая рамка вокруг первой выглядела бы сломанной вёрсткой.
 */
function ScenarioTable({
  scenarios,
  summary,
  hours,
  windowLen,
}: {
  scenarios: ScenarioStatus[] | undefined;
  summary: Record<string, FollowSummary> | undefined;
  hours: number;
  windowLen: number;
}) {
  // Форма счёта — по НАЛИТЫМ мейкерским входам, то есть по сделкам, которые
  // действительно состоялись. Это и есть та выборка, на которой у двух сценариев
  // минус оказался значимым: по выставленным планам, которых никто не налил,
  // счёт вообще не определён.
  const shapes = useJournalShapes(hours, "follow", windowLen);
  // Сценарии, у которых есть ИСТОРИЯ, но которых уже нет в настройке.
  //
  // Убрав правило из FOLLOW_SCENARIOS, оператор не стирает его строки из журнала
  // — и если показывать только настроенные, сотни сигналов молча исчезли бы с
  // экрана вместе с правилом. Это ровно тот случай, когда таблица врёт умолчанием:
  // «у этого сценария ничего нет» и «этот сценарий больше не запущен» — разные
  // утверждения, и второе обязано быть сказано вслух.
  const configured = new Set((scenarios ?? []).map((s) => s.id));
  const retired = Object.keys(summary ?? {})
    .filter((id) => !configured.has(id))
    .sort();

  if ((!scenarios || scenarios.length === 0) && retired.length === 0) {
    return (
      <section>
        <h3 className="mb-2 text-xs font-medium text-[var(--color-muted)]">
          сценарии — какое правило чего стоит
        </h3>
        <Empty>трейдер не ответил про сценарии</Empty>
      </section>
    );
  }
  return (
    <section>
      <h3 className="mb-2 text-xs font-medium text-[var(--color-muted)]">
        сценарии — какое правило чего стоит
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-[var(--color-muted)]">
            <tr>
              <th className="pb-2 font-normal">сценарий</th>
              <th className="pb-2 font-normal">потолок</th>
              <th className="pb-2 text-right font-normal">сутки, $</th>
              <th className="pb-2 text-right font-normal">сигналов</th>
              <th className="pb-2 text-right font-normal">ордеров</th>
              <th className="pb-2 text-right font-normal">мейкер</th>
              <th className="pb-2 text-right font-normal">тейкер</th>
              <RateHead />
              <th className="pb-2 text-right font-normal">форма счёта</th>
              <th className="pb-2 font-normal">режим</th>
            </tr>
          </thead>
          <tbody>
            {(scenarios ?? []).map((sc) => {
              const s = summary?.[sc.id];
              return (
                <tr key={sc.id} className="border-t border-[var(--color-border)]">
                  <td className="num py-1.5">{sc.id}</td>
                  <td className="py-1.5 text-[var(--color-muted)]">{sc.mode_env || "—"}</td>
                  <td className="num py-1.5 text-right">{sc.daily_cap || "—"}</td>
                  {/* Счётчики — из ЖУРНАЛА, а не из нот: ноты считают за этот
                      запуск трейдера и обнуляются рестартом, а окно сводки
                      задаёт переключатель наверху. */}
                  <td className="num py-1.5 text-right">{s ? s.signals : "—"}</td>
                  <td className="num py-1.5 text-right">{s ? s.placed : "—"}</td>
                  <td className="num py-1.5 text-right">
                    {s && s.settled > 0 ? signedDollars(s.shadow_maker_pnl) : "—"}
                  </td>
                  <td className="num py-1.5 text-right">
                    {s && s.taker_priced > 0 ? signedDollars(s.shadow_taker_pnl) : "—"}
                  </td>
                  {/* Удельный счёт — по МЕЙКЕРСКОЙ сумме: форма и ставка
                      считаются по shadow_maker_pnl, и ставка обязана стоять у
                      того числа, из которого посчитана. */}
                  <RateCell shape={shapeOf(shapes, sc.id)} hours={hours} />
                  <td className="py-1.5 text-right">
                    <PnLShape shape={shapeOf(shapes, sc.id)} />
                  </td>
                  <td className="py-1.5">
                    <ScenarioModeSwitch id={sc.id} ceiling={sc.mode_env} />
                  </td>
                </tr>
              );
            })}
            {retired.map((id) => {
              const s = summary?.[id];
              return (
                <tr key={id} className="border-t border-[var(--color-border)] opacity-60">
                  <td className="num py-1.5">{id}</td>
                  <td className="py-1.5 text-[var(--color-muted)]" colSpan={2}>
                    не запущен
                  </td>
                  <td className="num py-1.5 text-right">{s ? s.signals : "—"}</td>
                  <td className="num py-1.5 text-right">{s ? s.placed : "—"}</td>
                  <td className="num py-1.5 text-right">
                    {s && s.settled > 0 ? signedDollars(s.shadow_maker_pnl) : "—"}
                  </td>
                  <td className="num py-1.5 text-right">
                    {s && s.taker_priced > 0 ? signedDollars(s.shadow_taker_pnl) : "—"}
                  </td>
                  <RateCell shape={shapeOf(shapes, id)} hours={hours} />
                  <td className="py-1.5 text-right">
                    <PnLShape shape={shapeOf(shapes, id)} />
                  </td>
                  {/* Переключателя нет намеренно: режим у правила, которого нет
                      в настройке, менять не за что — трейдер его не читает. */}
                  <td className="py-1.5 text-[var(--color-muted)]">—</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Удельный счёт и приговор форме — строкой на сценарий.
          Сбор здесь ОСТАНОВЛЕН 2026-08-28, и ставка в час поэтому не «сколько
          приносит», а «сколько приносило»: срок закрывается последней строкой
          журнала, а не «сейчас», — иначе остановленный сценарий размывался бы
          простоем и с каждым днём выглядел бы всё безобиднее. Приговор форме
          объясняет уже известный итог: у двух правил минус не случайный, а
          собран из ровных потерь. */}
      <Note label="ставка и хрупкость — по сценариям">
        <ul className="space-y-1 text-xs text-[var(--color-muted)]">
          {[...(scenarios ?? []).map((sc) => sc.id), ...retired].map((id) => {
            const shape = shapeOf(shapes, id);
            return (
              <li key={id}>
                <span className="num">{id}</span> —{" "}
                <b style={{ color: rateTone(shape, hours) }}>{rateWord(shape, hours)}</b>;{" "}
                <span style={{ color: fragilityTone(shape) }}>{fragility(shape)}</span>.
              </li>
            );
          })}
        </ul>
      </Note>
      <Note label="как читать: потолок, прочерки и снятые правила">
        Потолок — то, выше чего кнопка режима не поднимет: он задан
        <span className="num"> FOLLOW_SCENARIOS</span> и правится только выкаткой. Прочерк в
        деньгах означает «считать было не по чему»: у мейкерской гипотезы это
        несведённые окна, у тейкерской — сигналы, у которых в тот момент не было
        стакана. Сравнивать две суммы можно лишь с оглядкой на то, что посчитаны
        они по РАЗНОМУ числу сигналов.
        {retired.length > 0 && (
          <>
            {" "}
            Строки <b>«не запущен»</b> — правила, которых больше нет в{" "}
            <span className="num">FOLLOW_SCENARIOS</span>: их сигналы остались в журнале и
            считаются в общем итоге выше, но новых не появится.
          </>
        )}
      </Note>
    </section>
  );
}

function Row({ signal }: { signal: FollowSignal }) {
  const when = new Date(signal.detected_at);
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="num py-1.5 whitespace-nowrap">
        {when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
      </td>
      <td className="py-1.5 whitespace-nowrap">
        {/* Сценарий: по одному удару кита законно рождается несколько строк, по
            одной на откликнувшееся правило. Без имени они выглядели бы дублями. */}
        <span className="num text-[var(--color-muted)]">{scenarioLabel(signal)}</span>
      </td>
      <td className="py-1.5">
        {/* Тот же ярлык, что в карточке: лента и карточка описывают одного и того
            же кита, и подписаны они обязаны быть одинаково. Полный адрес — в
            подсказке, потому что имя кошелёк выбирает себе сам. */}
        <Link
          to={`/wallet/${signal.wallet}`}
          className={`underline decoration-dotted underline-offset-2${
            signal.wallet_name ? "" : " num"
          }`}
          title={signal.wallet}
        >
          {walletLabel(signal)}
        </Link>
      </td>
      <td className="num py-1.5 text-right">{formatDollars(signal.whale.avg_usd)}</td>
      <td className="num py-1.5 text-right whitespace-nowrap">
        <span style={{ color: toneColor(signTone(signal.whale.pnl_usd)) }}>
          {signedDollars(signal.whale.pnl_usd)}
        </span>{" "}
        <span className="text-[var(--color-muted)]">{depthLabel(signal)}</span>
      </td>
      <td className="py-1.5">
        <Link
          to={`/market/${signal.condition_id}`}
          className="underline decoration-dotted underline-offset-2"
        >
          {signal.slug || signal.condition_id.slice(0, 10)}
        </Link>
      </td>
      <td
        className="py-1.5"
        style={{ color: signal.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)" }}
      >
        {/* Исход КИТА. Наш — рядом, и только когда он другой: у следования
            повторять одно и то же дважды значило бы засорить строку. */}
        {signal.outcome_name || signal.side}
        {against(signal) && (
          <span className="text-[var(--color-muted)]" title="сценарий ставит ПРОТИВ кита">
            {" "}
            → мы против
          </span>
        )}
      </td>
      <td className="num py-1.5 text-right whitespace-nowrap">
        {formatDollars(signal.whale_usd)} @ {signal.whale_price}
      </td>
      <td className="num py-1.5 text-right whitespace-nowrap">
        {/* Прочерк, а не ноль: у погашенного правилом сигнала плана не было. */}
        {signal.plan_usd === "0" ? "—" : `${formatDollars(signal.plan_usd)} @ ${signal.plan_price}`}
      </td>
      <td className="py-1.5">
        {outcomeLabel(signal.outcome)}
        {signal.rule && (
          <span className="text-[var(--color-muted)]"> · {ruleLabel(signal.rule)}</span>
        )}
        {signal.crossed && (
          <span className="text-[var(--color-warn)]"> · пересёк стакан</span>
        )}
      </td>
      <td className="num py-1.5 text-right">
        {/* Прочерк отличается от нуля: «не сводили» и «вышло ноль» разные ответы. */}
        {signal.resolved === undefined
          ? "—"
          : signal.maker_filled
            ? signedDollars(signal.shadow_maker_pnl)
            : "не вошли"}
      </td>
      <td className="num py-1.5 text-right">
        {signal.shadow_taker_pnl === undefined ? "—" : signedDollars(signal.shadow_taker_pnl)}
      </td>
    </tr>
  );
}
